import React from "react";

import { Box } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import ModernModal from "../../../../../common/ModernModal";
import {
  ModernFormField,
  ModernSubmitButton,
  ModernFormAlert,
} from "../../../../../common/ModernForm";
import { projectsService } from "../../../../services";

interface Props {
  handleClose: () => void;
  open: boolean;
  projectId: string;
  projectName: string;
  projectDescription: string;
}

export default function EditDialog({
  handleClose,
  open,
  projectId,
  projectName,
  projectDescription,
}: Props) {
  const { mutateAsync, isError, error, isPending, isSuccess } = useMutation({
    mutationKey: ["edit-project"],
    mutationFn: (values: {
      project_name: string;
      project_description: string;
    }) =>
      projectsService.updateProject({
        projectId,
        project_name: values.project_name,
        project_description: values.project_description,
      }),
  });

  const handleEditProject = async (values: {
    project_name: string;
    project_description: string;
  }) => {
    await mutateAsync(values);
  };

  if (isSuccess) {
    setTimeout(() => {
      handleClose();
      window.location.reload();
    }, 2000);
  }

  return (
    <ModernModal
      open={open}
      onClose={handleClose}
      title="Edit Project"
      maxWidth="sm"
    >
      <Formik
        initialValues={{
          project_name: projectName,
          project_description: projectDescription,
        }}
        validationSchema={Yup.object({
          project_name: Yup.string().required("Required"),
        })}
        onSubmit={handleEditProject}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isError && (
                <ModernFormAlert severity="error">
                  {error?.message || "An error occurred"}
                </ModernFormAlert>
              )}

              {isSuccess && (
                <ModernFormAlert severity="success">
                  Project updated successfully!
                </ModernFormAlert>
              )}

              <ModernFormField
                label="Project Name"
                name="project_name"
                error={!!formik.errors.project_name}
                helperText={formik.errors.project_name}
                value={formik.values.project_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                required
              />

              <ModernFormField
                label="Project Description"
                name="project_description"
                multiline
                rows={3}
                error={!!formik.errors.project_description}
                helperText={formik.errors.project_description}
                value={formik.values.project_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              <ModernSubmitButton
                label="Save Changes"
                type="submit"
                loading={isPending}
                disabled={isPending || isSuccess}
              />
            </Box>
          </form>
        )}
      </Formik>
    </ModernModal>
  );
}
